import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useCreateCallingRuleMutation, useUpdateCallingRuleMutation } from "@/store/admin_api";
import type {
  CallingRegionOption,
  CallingRule,
  CallingRuleFormData,
  CallingWeek,
  CallingWeekDay,
} from "@/model/interface/CallingRule";
import { CallingHolidayRule } from "@/enum/CallingHolidayRule";
import { CallingRecordedMessages } from "@/enum/CallingRecordedMessages";
import { CallingRuleBasis } from "@/enum/CallingRuleBasis";
import { RECORDING_CONSENT_LABELS, RecordingConsent } from "@/enum/RecordingConsent";
import {
  CALLING_WEEK_DAYS,
  FEDERAL_REGION,
  HOLIDAY_RULE_LABELS,
  RECORDED_MESSAGES_LABELS,
  RULE_BASIS_LABELS,
} from "@/util/callingRules";
import { apiErrorMessage } from "@/util/apiError";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type CallingRuleDialogMode = "create" | "edit" | "schedule";

interface CallingRuleDialogProps {
  open: boolean;
  mode: CallingRuleDialogMode;
  rule: CallingRule | null;
  regions: CallingRegionOption[];
  onOpenChange: (open: boolean) => void;
}

const DEFAULT_WINDOW = { start: "08:00", end: "21:00" };

const EMPTY_FORM: CallingRuleFormData = {
  region: FEDERAL_REGION,
  week: {
    monday: DEFAULT_WINDOW,
    tuesday: DEFAULT_WINDOW,
    wednesday: DEFAULT_WINDOW,
    thursday: DEFAULT_WINDOW,
    friday: DEFAULT_WINDOW,
    saturday: DEFAULT_WINDOW,
    sunday: DEFAULT_WINDOW,
  },
  holidayRule: CallingHolidayRule.NONE,
  maxCallsPerDay: null,
  recordedMessages: CallingRecordedMessages.WRITTEN_CONSENT,
  recordingConsent: RecordingConsent.NEEDS_REVIEW,
  timeZone: null,
  effectiveFrom: null,
  effectiveTo: null,
  source: null,
  basis: CallingRuleBasis.NEEDS_REVIEW,
  notes: null,
};

const DIALOG_TITLES: Record<CallingRuleDialogMode, string> = {
  create: "Add a calling rule",
  edit: "Edit calling rule",
  schedule: "Schedule a change",
};

function formFromRule(rule: CallingRule, mode: CallingRuleDialogMode): CallingRuleFormData {
  return {
    region: rule.region,
    week: rule.week,
    holidayRule: rule.holidayRule,
    maxCallsPerDay: rule.maxCallsPerDay ?? null,
    recordedMessages: rule.recordedMessages,
    recordingConsent: rule.recordingConsent,
    timeZone: rule.timeZone ?? null,
    effectiveFrom: mode === "schedule" ? null : (rule.effectiveFrom ?? null),
    effectiveTo: mode === "schedule" ? null : (rule.effectiveTo ?? null),
    source: rule.source ?? null,
    basis: rule.basis,
    notes: rule.notes ?? null,
  };
}

export function CallingRuleDialog({ open, mode, rule, regions, onOpenChange }: CallingRuleDialogProps) {
  const [form, setForm] = useState<CallingRuleFormData>(EMPTY_FORM);
  const [createRule, { isLoading: isCreating }] = useCreateCallingRuleMutation();
  const [updateRule, { isLoading: isUpdating }] = useUpdateCallingRuleMutation();
  const isSaving = isCreating || isUpdating;

  const updateForm = (changes: Partial<CallingRuleFormData>) => {
    setForm((current) => ({ ...current, ...changes }));
  };

  const updateDay = (day: CallingWeekDay, changes: Partial<{ start: string; end: string }> | null) => {
    setForm((current) => {
      const currentWindow = current.week[day] ?? DEFAULT_WINDOW;
      const week: CallingWeek = { ...current.week, [day]: changes === null ? null : { ...currentWindow, ...changes } };
      return { ...current, week };
    });
  };

  const handleSave = async () => {
    if (mode === "schedule" && !form.effectiveFrom) {
      toast.error("Pick the date the change starts");
      return;
    }
    const payload: CallingRuleFormData = {
      ...form,
      timeZone: form.timeZone?.trim() || null,
      source: form.source?.trim() || null,
      notes: form.notes?.trim() || null,
      effectiveFrom: form.effectiveFrom || null,
      effectiveTo: form.effectiveTo || null,
    };
    try {
      if (mode === "edit" && rule) {
        await updateRule({ ruleId: rule.id, data: payload }).unwrap();
        toast.success("Calling rule saved");
      } else {
        await createRule(payload).unwrap();
        toast.success(mode === "schedule" ? "Change scheduled" : "Calling rule added");
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not save the calling rule"));
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    setForm(rule ? formFromRule(rule, mode) : EMPTY_FORM);
  }, [open, rule, mode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{DIALOG_TITLES[mode]}</DialogTitle>
          <DialogDescription>
            {mode === "schedule"
              ? "The new hours start on the date you pick. The current rule ends the day before."
              : "Calls and texts follow the federal rule and the lead's state rule. The stricter one wins."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Region</Label>
              <Select
                value={form.region}
                onValueChange={(region) => updateForm({ region })}
                disabled={mode !== "create"}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((region) => (
                    <SelectItem key={region.code} value={region.code}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Time zone override</Label>
              <Input
                value={form.timeZone ?? ""}
                onChange={(event) => updateForm({ timeZone: event.target.value })}
                placeholder="America/Chicago"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Calling hours</Label>
            <div className="space-y-2 rounded-lg border p-3">
              {CALLING_WEEK_DAYS.map(({ day, label }) => {
                const dayWindow = form.week[day];
                return (
                  <div key={day} className="flex flex-wrap items-center gap-3">
                    <label className="flex w-36 items-center gap-2 text-sm">
                      <Checkbox
                        checked={Boolean(dayWindow)}
                        onCheckedChange={(checked) => updateDay(day, checked === true ? {} : null)}
                      />
                      {label}
                    </label>
                    {dayWindow ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="time"
                          className="w-32"
                          value={dayWindow.start}
                          onChange={(event) => updateDay(day, { start: event.target.value })}
                        />
                        <span className="text-sm text-muted-foreground">to</span>
                        <Input
                          type="time"
                          className="w-32"
                          value={dayWindow.end}
                          onChange={(event) => updateDay(day, { end: event.target.value })}
                        />
                      </div>
                    ) : (
                      <span className="text-sm text-muted-foreground">No calls</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Holidays</Label>
              <Select
                value={form.holidayRule}
                onValueChange={(holidayRule) => updateForm({ holidayRule: holidayRule as CallingHolidayRule })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(CallingHolidayRule).map((holidayRule) => (
                    <SelectItem key={holidayRule} value={holidayRule}>
                      {HOLIDAY_RULE_LABELS[holidayRule]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Calls per person per 24 hours</Label>
              <Input
                type="number"
                min={1}
                value={form.maxCallsPerDay ?? ""}
                onChange={(event) =>
                  updateForm({ maxCallsPerDay: event.target.value === "" ? null : Number(event.target.value) })
                }
                placeholder="No cap"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Recorded messages</Label>
              <Select
                value={form.recordedMessages}
                onValueChange={(recordedMessages) =>
                  updateForm({ recordedMessages: recordedMessages as CallingRecordedMessages })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(CallingRecordedMessages).map((recordedMessages) => (
                    <SelectItem key={recordedMessages} value={recordedMessages}>
                      {RECORDED_MESSAGES_LABELS[recordedMessages]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Call recording consent</Label>
              <Select
                value={form.recordingConsent}
                onValueChange={(recordingConsent) =>
                  updateForm({ recordingConsent: recordingConsent as RecordingConsent })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(RecordingConsent).map((recordingConsent) => (
                    <SelectItem key={recordingConsent} value={recordingConsent}>
                      {RECORDING_CONSENT_LABELS[recordingConsent]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                All parties means a call to this state needs a recording announcement. Needs review is treated the same
                way, so nothing is recorded silently while the rule is undecided.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Basis</Label>
              <Select value={form.basis} onValueChange={(basis) => updateForm({ basis: basis as CallingRuleBasis })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(CallingRuleBasis).map((basis) => (
                    <SelectItem key={basis} value={basis}>
                      {RULE_BASIS_LABELS[basis]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Starts</Label>
              <Input
                type="date"
                value={form.effectiveFrom ?? ""}
                onChange={(event) => updateForm({ effectiveFrom: event.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Ends</Label>
              <Input
                type="date"
                value={form.effectiveTo ?? ""}
                onChange={(event) => updateForm({ effectiveTo: event.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Source</Label>
            <Input
              value={form.source ?? ""}
              onChange={(event) => updateForm({ source: event.target.value })}
              placeholder="Fla. Stat. 501.616(6)"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea
              rows={3}
              value={form.notes ?? ""}
              onChange={(event) => updateForm({ notes: event.target.value })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} disabled={isSaving}>
            {mode === "schedule" ? "Schedule" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
